import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { UserCog, Plus, Trash2, KeyRound, Loader2, Star, Pencil, X } from 'lucide-react';
import { Section, TextField } from './FormFields';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const ROLE_BADGE = {
  manager: 'bg-blue-100 text-blue-700',
  agent: 'bg-slate-200 text-slate-600',
};

const TeamAccountsTab = ({ currentRole, currentRegions = [], focusRegion = null, onFocusRegionClear }) => {
  const [members, setMembers] = useState([]);
  const [regions, setRegions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [tempPassword, setTempPassword] = useState(null);
  const [form, setForm] = useState({ username: '', name: '', email: '', role: 'agent', regions: [] });
  const [busyUsername, setBusyUsername] = useState(null);
  const [editingUsername, setEditingUsername] = useState(null);
  const [editRegions, setEditRegions] = useState([]);
  const [editingDetails, setEditingDetails] = useState(null);
  const [detailsForm, setDetailsForm] = useState({ username: '', name: '', email: '', role: 'agent' });
  const [savingDetails, setSavingDetails] = useState(false);

  const isManager = currentRole === 'manager';
  const availableRegions = isManager ? regions.filter((r) => currentRegions.includes(r.code)) : regions;
  const regionName = (code) => regions.find((r) => r.code === code)?.name || code;

  useEffect(() => {
    // "Fix it" shortcut from Region Health: jump here pre-filtered to the
    // affected region, with the create-account form pre-set to Manager +
    // that region so Admin can create an owner in one flow.
    if (focusRegion) {
      setForm((f) => ({ ...f, role: 'manager', regions: [focusRegion] }));
    }
  }, [focusRegion]);

  const visibleMembers = focusRegion ? members.filter((m) => (m.regions || []).includes(focusRegion)) : members;

  const fetchAll = async (silent = false) => {
    if (!silent) setLoading(true);
    try {
      const [membersRes, regionsRes] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/admin/team-accounts`, { withCredentials: true }),
        axios.get(`${BACKEND_URL}/api/admin/leads/regions`, { withCredentials: true }),
      ]);
      setMembers(membersRes.data.members || []);
      setRegions((regionsRes.data.regions || []).filter((r) => r.code !== 'default'));
    } catch {
      if (!silent) toast.error('Failed to load team accounts');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAll();
    // A member's own self-service profile edit (Account tab) or a change
    // made in another session isn't reflected here otherwise — poll
    // silently so the list never needs a manual reload/tab-switch to catch up.
    const interval = setInterval(() => fetchAll(true), 60000);
    return () => clearInterval(interval);
  }, []);

  const toggleRegion = (code) => {
    setForm((f) => ({
      ...f,
      regions: f.regions.includes(code) ? f.regions.filter((r) => r !== code) : [...f.regions, code],
    }));
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!form.username.trim() || !form.name.trim() || !form.email.trim()) return;
    setSaving(true);
    try {
      const res = await axios.post(`${BACKEND_URL}/api/admin/team-accounts`, {
        username: form.username.trim(),
        name: form.name.trim(),
        email: form.email.trim(),
        role: isManager ? 'agent' : form.role,
        regions: form.regions,
      }, { withCredentials: true });
      setTempPassword({ username: res.data.username, password: res.data.temp_password });
      setForm({ username: '', name: '', email: '', role: 'agent', regions: [] });
      toast.success(`${res.data.role === 'manager' ? 'Manager' : 'Agent'} account created`);
      fetchAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to create account');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (username) => {
    if (!window.confirm(`Remove ${username}'s account? Their assigned leads will become unassigned.`)) return;
    setBusyUsername(username);
    try {
      await axios.delete(`${BACKEND_URL}/api/admin/team-accounts/${username}`, { withCredentials: true });
      setMembers((prev) => prev.filter((m) => m.username !== username));
      toast.success('Account removed');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to remove account');
    } finally {
      setBusyUsername(null);
    }
  };

  const handleToggleOwner = async (username, region, isOwner) => {
    try {
      await axios.put(`${BACKEND_URL}/api/admin/team-accounts/${username}/region-owner`,
        { region, is_owner: isOwner }, { withCredentials: true });
      toast.success(isOwner ? `${username} is now the region owner for ${regionName(region)}` : 'Region owner removed');
      fetchAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update region owner');
    }
  };

  const handleResetPassword = async (username) => {
    setBusyUsername(username);
    try {
      const res = await axios.post(`${BACKEND_URL}/api/admin/team-accounts/${username}/reset-password`, {}, { withCredentials: true });
      setTempPassword({ username, password: res.data.temp_password });
      toast.success('New temporary password generated');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to reset password');
    } finally {
      setBusyUsername(null);
    }
  };

  const startEditRegions = (m) => {
    setEditingUsername(m.username);
    setEditRegions(m.regions || []);
  };

  const toggleEditRegion = (code) => {
    setEditRegions((prev) => (prev.includes(code) ? prev.filter((r) => r !== code) : [...prev, code]));
  };

  const saveEditRegions = async (m) => {
    if (m.role === 'manager' && editRegions.length === 0) {
      toast.error('Manager accounts must have at least one region');
      return;
    }
    // Owner Scope Guardrail: warn before a region narrowing would silently
    // strip this member's ownership of a region they currently hold.
    const droppedOwned = (m.owns_regions || []).filter((code) => !editRegions.includes(code));
    if (droppedOwned.length > 0) {
      const names = droppedOwned.map(regionName).join(', ');
      if (!window.confirm(`${m.name} currently owns ${names}. Removing that region will also remove them as its owner. Continue?`)) {
        return;
      }
    }
    setBusyUsername(m.username);
    try {
      await axios.put(`${BACKEND_URL}/api/admin/team-accounts/${m.username}`, { regions: editRegions }, { withCredentials: true });
      toast.success('Regions updated');
      setEditingUsername(null);
      fetchAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update regions');
    } finally {
      setBusyUsername(null);
    }
  };

  const [loadingDetails, setLoadingDetails] = useState(false);

  const startEditDetails = async (m) => {
    setEditingDetails(m.username);
    setDetailsForm({ username: m.username, name: m.name, email: m.email, role: m.role });
    setLoadingDetails(true);
    // Always read the LATEST record — this member may have self-updated
    // their own name/email (Account tab) since this list was last fetched.
    try {
      const res = await axios.get(`${BACKEND_URL}/api/admin/team-accounts`, { withCredentials: true });
      const freshMembers = res.data.members || [];
      setMembers(freshMembers);
      const fresh = freshMembers.find((x) => x.username === m.username);
      if (fresh) setDetailsForm({ username: fresh.username, name: fresh.name, email: fresh.email, role: fresh.role });
    } catch {
      // Keep the snapshot already shown above if the refresh fails.
    } finally {
      setLoadingDetails(false);
    }
  };

  const saveDetails = async () => {
    const original = members.find((m) => m.username === editingDetails);
    if (!original) return;
    if (!detailsForm.username.trim() || !detailsForm.name.trim() || !detailsForm.email.trim()) {
      toast.error('Username, name and email are all required');
      return;
    }
    const roleChanging = currentRole === 'admin' && detailsForm.role !== original.role;
    if (roleChanging && detailsForm.role === 'manager' && (original.regions || []).length === 0) {
      toast.error(`${original.name} has no assigned region(s) yet — add one before making them a Manager`);
      return;
    }
    if (roleChanging && detailsForm.role === 'agent' && (original.owns_regions || []).length > 0) {
      const names = original.owns_regions.map(regionName).join(', ');
      if (!window.confirm(`${original.name} currently owns ${names}. Switching to Agent will remove their ★ ownership (region access is kept). Continue?`)) {
        return;
      }
    }
    setSavingDetails(true);
    try {
      const payload = {
        name: detailsForm.name.trim(),
        email: detailsForm.email.trim(),
        username: detailsForm.username.trim(),
      };
      if (roleChanging) payload.role = detailsForm.role;
      await axios.put(`${BACKEND_URL}/api/admin/team-accounts/${original.username}`, payload, { withCredentials: true });
      toast.success('Account details updated');
      setEditingDetails(null);
      await fetchAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update account details');
    } finally {
      setSavingDetails(false);
    }
  };

  return (
    <Section title="Team Accounts" description={isManager
      ? 'Create and manage Agent logins within your assigned region(s). Agents only see leads explicitly assigned to them.'
      : 'Create Manager (full-privilege, region-scoped) and Agent (assigned-leads-only) logins. Everyone signs in from the same admin login page.'}>
      <div data-testid="team-accounts-tab" className="space-y-6">
        {focusRegion && (
          <div className="bg-amber-50 border border-amber-300 rounded-xl p-4 flex items-center justify-between gap-3" data-testid="team-accounts-region-filter-banner">
            <p className="text-sm text-amber-800">
              Showing accounts assigned to <strong>{regionName(focusRegion)}</strong> — this region has no owner yet.
              Click the star next to {regionName(focusRegion)} below to set one, or create a new Manager (pre-filled below).
            </p>
            <button
              type="button"
              onClick={() => onFocusRegionClear && onFocusRegionClear()}
              data-testid="team-accounts-clear-region-filter"
              className="text-amber-700 hover:text-amber-900 text-sm font-medium flex-shrink-0 underline"
            >
              Show all accounts
            </button>
          </div>
        )}
        {tempPassword && (
          <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-4 flex items-center justify-between gap-3" data-testid="temp-password-banner">
            <p className="text-sm text-emerald-800">
              Temporary password for <strong>{tempPassword.username}</strong>: <code className="bg-white px-2 py-0.5 rounded border border-emerald-200 font-mono" data-testid="temp-password-value">{tempPassword.password}</code>
              <span className="block text-xs text-emerald-600 mt-0.5">Copy this now — it won't be shown again. They'll be asked to set a new password on first login.</span>
            </p>
            <button type="button" onClick={() => setTempPassword(null)} className="text-emerald-600 hover:text-emerald-800 text-sm font-medium flex-shrink-0">Dismiss</button>
          </div>
        )}

        <form onSubmit={handleCreate} className="bg-slate-50 border border-slate-200 rounded-xl p-4 space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <TextField label="Username" value={form.username} onChange={(v) => setForm((f) => ({ ...f, username: v }))} placeholder="jane.agent" testId="new-member-username-input" />
            <TextField label="Full name" value={form.name} onChange={(v) => setForm((f) => ({ ...f, name: v }))} placeholder="Jane Doe" testId="new-member-name-input" />
            <TextField label="Email" value={form.email} onChange={(v) => setForm((f) => ({ ...f, email: v }))} placeholder="jane@example.com" testId="new-member-email-input" />
          </div>
          {!isManager && (
            <div>
              <label className="text-sm font-medium text-slate-600 block mb-1">Role</label>
              <select
                value={form.role}
                onChange={(e) => setForm((f) => ({ ...f, role: e.target.value }))}
                data-testid="new-member-role-select"
                className="border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-slate-900"
              >
                <option value="manager">Manager (full-privilege)</option>
                <option value="agent">Agent</option>
              </select>
            </div>
          )}
          <div>
            <label className="text-sm font-medium text-slate-600 block mb-1">Region(s)</label>
            <div className="flex flex-wrap gap-2" data-testid="new-member-regions">
              {availableRegions.map((r) => (
                <button
                  type="button"
                  key={r.code}
                  onClick={() => toggleRegion(r.code)}
                  data-testid={`region-toggle-${r.code}`}
                  className={`text-xs font-medium px-3 py-1.5 rounded-full border transition-colors ${
                    form.regions.includes(r.code)
                      ? 'bg-slate-900 text-white border-slate-900'
                      : 'bg-white text-slate-600 border-slate-200 hover:border-slate-400'
                  }`}
                >
                  {r.name}
                </button>
              ))}
            </div>
          </div>
          <button
            type="submit"
            disabled={saving || !form.username.trim() || !form.name.trim() || !form.email.trim()}
            data-testid="create-member-button"
            className="inline-flex items-center gap-2 bg-slate-900 text-white px-4 py-2.5 rounded-lg font-medium text-sm hover:bg-slate-800 disabled:opacity-40 transition-colors"
          >
            <Plus className="w-4 h-4" /> Create Account
          </button>
        </form>

        {loading ? (
          <p className="text-sm text-slate-400 italic">Loading team accounts…</p>
        ) : (
          <div className="divide-y divide-slate-100 border border-slate-200 rounded-xl overflow-hidden" data-testid="members-list">
            {visibleMembers.map((m) => (
              <div key={m.username} className="flex items-center gap-3 p-3 bg-white flex-wrap" data-testid={`member-row-${m.username}`}>
                <UserCog className="w-4 h-4 text-slate-400 flex-shrink-0" />
                <div className="min-w-[140px]">
                  <p className="text-sm font-medium text-slate-800">{m.name} <span className="text-slate-400">({m.username})</span></p>
                  <p className="text-xs text-slate-500">{m.email}</p>
                </div>
                <span className={`text-[11px] font-semibold px-2 py-0.5 rounded-full ${ROLE_BADGE[m.role] || ''}`}>{m.role}</span>
                {editingUsername === m.username ? (
                  <div className="flex-1 min-w-[220px]">
                    <div className="flex flex-wrap gap-1.5 mb-2" data-testid={`edit-regions-toggles-${m.username}`}>
                      {availableRegions.map((r) => (
                        <button
                          key={r.code}
                          type="button"
                          onClick={() => toggleEditRegion(r.code)}
                          data-testid={`edit-region-toggle-${m.username}-${r.code}`}
                          className={`text-[11px] font-medium px-2.5 py-1 rounded-full border transition-colors ${
                            editRegions.includes(r.code)
                              ? 'bg-slate-900 text-white border-slate-900'
                              : 'bg-white text-slate-600 border-slate-200 hover:border-slate-400'
                          }`}
                        >
                          {r.name}
                        </button>
                      ))}
                    </div>
                    <div className="flex items-center gap-2">
                      <button
                        type="button"
                        onClick={() => saveEditRegions(m)}
                        disabled={busyUsername === m.username}
                        data-testid={`save-regions-button-${m.username}`}
                        className="text-xs bg-slate-900 text-white px-3 py-1.5 rounded-lg disabled:opacity-50"
                      >
                        {busyUsername === m.username ? 'Saving…' : 'Save regions'}
                      </button>
                      <button
                        type="button"
                        onClick={() => setEditingUsername(null)}
                        data-testid={`cancel-regions-button-${m.username}`}
                        className="text-xs text-slate-500 hover:text-slate-800"
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                ) : (
                  <div className="flex flex-wrap gap-1 flex-1">
                    {(m.regions || []).map((code) => {
                      const isOwner = (m.owns_regions || []).includes(code);
                      if (m.role === 'manager' && currentRole === 'admin') {
                        return (
                          <button
                            key={code}
                            type="button"
                            onClick={() => handleToggleOwner(m.username, code, !isOwner)}
                            data-testid={`region-owner-toggle-${m.username}-${code}`}
                            title={isOwner ? `Region owner of ${regionName(code)} — click to unset` : `Set as region owner of ${regionName(code)}`}
                            className={`text-[11px] font-medium px-2 py-0.5 rounded-full inline-flex items-center gap-1 border transition-colors ${
                              isOwner ? 'bg-amber-100 text-amber-700 border-amber-300' : 'bg-slate-100 text-slate-600 border-slate-200 hover:border-amber-300'
                            }`}
                          >
                            <Star className={`w-3 h-3 ${isOwner ? 'fill-amber-500 text-amber-500' : ''}`} /> {regionName(code)}
                          </button>
                        );
                      }
                      return (
                        <span key={code} className="text-[11px] bg-slate-100 text-slate-600 px-2 py-0.5 rounded-full">
                          {regionName(code)}{isOwner ? ' ★' : ''}
                        </span>
                      );
                    })}
                    <button
                      type="button"
                      onClick={() => startEditRegions(m)}
                      data-testid={`edit-regions-button-${m.username}`}
                      className="text-[11px] text-indigo-600 hover:text-indigo-800 underline px-1"
                    >
                      Edit regions
                    </button>
                  </div>
                )}
                <button
                  type="button"
                  onClick={() => startEditDetails(m)}
                  data-testid={`edit-details-button-${m.username}`}
                  className="text-xs font-medium text-slate-600 hover:text-slate-900 border border-slate-200 rounded-lg px-2.5 py-1.5 inline-flex items-center gap-1"
                >
                  <Pencil className="w-3.5 h-3.5" /> Edit details
                </button>
                <button
                  type="button"
                  onClick={() => handleResetPassword(m.username)}
                  disabled={busyUsername === m.username}
                  data-testid={`reset-password-button-${m.username}`}
                  className="text-xs font-medium text-slate-600 hover:text-slate-900 border border-slate-200 rounded-lg px-2.5 py-1.5 disabled:opacity-50 inline-flex items-center gap-1"
                >
                  {busyUsername === m.username ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <KeyRound className="w-3.5 h-3.5" />} Reset password
                </button>
                <button
                  type="button"
                  onClick={() => handleDelete(m.username)}
                  disabled={busyUsername === m.username}
                  data-testid={`delete-member-button-${m.username}`}
                  className="text-slate-400 hover:text-red-500 p-1.5"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            ))}
            {visibleMembers.length === 0 && (
              <p className="text-sm text-slate-400 italic p-4" data-testid="members-list-empty">
                {focusRegion ? `No account is assigned to ${regionName(focusRegion)} yet — create a Manager above (region pre-selected).` : 'No team accounts yet — create one above.'}
              </p>
            )}
          </div>
        )}

        {editingDetails && (
          <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4" data-testid="edit-details-modal">
            <div className="bg-white rounded-xl shadow-xl w-full max-w-md p-5 space-y-4">
              <div className="flex items-center justify-between">
                <h3 className="font-semibold text-slate-900 flex items-center gap-2">
                  Edit account details
                  {loadingDetails && <Loader2 className="w-3.5 h-3.5 animate-spin text-slate-400" data-testid="edit-details-refreshing" />}
                </h3>
                <button type="button" onClick={() => setEditingDetails(null)} data-testid="edit-details-close">
                  <X className="w-5 h-5 text-slate-400 hover:text-slate-700" />
                </button>
              </div>
              <TextField
                label="Username"
                value={detailsForm.username}
                onChange={(v) => setDetailsForm((f) => ({ ...f, username: v }))}
                testId="edit-details-username-input"
              />
              <TextField
                label="Full name"
                value={detailsForm.name}
                onChange={(v) => setDetailsForm((f) => ({ ...f, name: v }))}
                testId="edit-details-name-input"
              />
              <TextField
                label="Email"
                value={detailsForm.email}
                onChange={(v) => setDetailsForm((f) => ({ ...f, email: v }))}
                testId="edit-details-email-input"
              />
              {currentRole === 'admin' && (
                <div>
                  <label className="text-sm font-medium text-slate-600 block mb-1">Role</label>
                  <select
                    value={detailsForm.role}
                    onChange={(e) => setDetailsForm((f) => ({ ...f, role: e.target.value }))}
                    data-testid="edit-details-role-select"
                    className="border border-slate-200 rounded-lg px-3 py-2 text-sm w-full focus:outline-none focus:ring-2 focus:ring-slate-900"
                  >
                    <option value="manager">Manager (full-privilege)</option>
                    <option value="agent">Agent</option>
                  </select>
                  <p className="text-xs text-slate-400 mt-1">Region access stays exactly as assigned — changing role only changes ★ ownership eligibility.</p>
                </div>
              )}
              <div className="flex items-center gap-2 pt-1">
                <button
                  type="button"
                  onClick={saveDetails}
                  disabled={savingDetails || loadingDetails}
                  data-testid="edit-details-save-button"
                  className="flex-1 inline-flex items-center justify-center gap-2 bg-slate-900 text-white px-4 py-2.5 rounded-lg font-medium text-sm hover:bg-slate-800 disabled:opacity-50"
                >
                  {savingDetails ? <Loader2 className="w-4 h-4 animate-spin" /> : null} Save changes
                </button>
                <button
                  type="button"
                  onClick={() => setEditingDetails(null)}
                  data-testid="edit-details-cancel-button"
                  className="px-4 py-2.5 text-sm text-slate-500 hover:text-slate-800"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </Section>
  );
};

export default TeamAccountsTab;
